/**
 * Phase 126.C — Classroom Support Hub generator page.
 *
 * One file serves BOTH `/classroom-support/adhd/generator` and
 * `/classroom-support/giftedness/generator` — the profile is read from
 * the URL param so the marketing pages can deep-link directly.
 *
 * Auth: signed-in (free account) required. Anonymous visitors see an
 * inline "Sign in to generate (free)" wall instead of the form. The
 * Hub itself stays public.
 *
 * Privacy: nothing is stored. The teacher types the need, the page
 * generates a plan, the teacher exports the PDF — done.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Library,
  Loader2,
  LogIn,
  Sparkles,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import useDocumentMeta from "../../lib/useDocumentMeta";
import { useAuth } from "../../contexts/AuthContext";
import CompanionToolsRow from "../../components/classroom-support/CompanionToolsRow";
import { AtomLoader, GENERATOR_LOADER_MESSAGES } from "../../components/AtomLoader";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PROFILE_META = {
  adhd: {
    label: "ADHD support",
    headline: "ADHD support — plan",
    tagline:
      "Type the situation. Get back a teacher-voice plan with strategies threaded into context, a single 'try this first' move, look-fors, and a printable PDF.",
    placeholder:
      "e.g., Grade 5, bright student who shuts down on writing tasks that look long, blurts out during instruction, loses materials by mid-period.",
    icon: Wrench,
  },
  giftedness: {
    label: "Gifted extension",
    headline: "Gifted extension — plan",
    tagline:
      "Type the situation. Get back an extension plan that adds depth (not volume) — open-ended challenge, metacognition, productive-failure framing.",
    placeholder:
      "e.g., Grade 6 who finishes the math investigation in five minutes, asks 'is this all?', then goes quiet for the rest of the period.",
    icon: Sparkles,
  },
  autism: {
    label: "Autism support",
    headline: "Autism support — plan",
    tagline:
      "Type the situation. Get back a plan that builds predictability, makes expectations explicit, considers sensory load, and respects how the student communicates — strengths-first, identity-affirming.",
    placeholder:
      "e.g., Grade 4, autistic student who melts down at unexpected schedule changes and shuts down in unstructured group work.",
    icon: Wrench,
  },
};

const PROFILE_TO_REF_PAGE = {
  adhd: "/classroom-support/adhd",
  giftedness: "/classroom-support/giftedness",
  autism: "/classroom-support/autism",
};

const PROFILE_REF_LABEL = {
  adhd: "ADHD",
  giftedness: "giftedness",
  autism: "autism",
};

// URL "/classroom-support/giftedness/generator" → backend slug
// "gifted" (the taxonomy uses the shorter tag).
const URL_TO_API_PROFILE = {
  adhd: "adhd",
  giftedness: "gifted",
  autism: "autism",
};

export default function ClassroomSupportGenerator() {
  const { profile: urlProfile } = useParams();
  const [searchParams] = useSearchParams();
  const requestedPlanId = searchParams.get("plan");
  const profileKey = (urlProfile || "").toLowerCase();
  const meta = PROFILE_META[profileKey];
  const apiProfile = URL_TO_API_PROFILE[profileKey];
  const { user, loading: authLoading, signIn } = useAuth();

  const [situation, setSituation] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [exportingBranded, setExportingBranded] = useState(false);
  const [exportingUnbranded, setExportingUnbranded] = useState(false);
  const [plan, setPlan] = useState(null);
  const [quota, setQuota] = useState(null); // { remaining, used, limit, resets_at, exempt }
  const [capDismissed, setCapDismissed] = useState(false);

  // Phase 126.K — when arriving via the library "Open" link, fetch the
  // saved plan and render it as if it had just been generated.
  useEffect(() => {
    if (!user || !apiProfile || !requestedPlanId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${apiBase()}/classroom-support/plans/${encodeURIComponent(requestedPlanId)}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const doc = await res.json();
        if (cancelled) return;
        const body = doc.plan_body || {};
        setSituation(doc.situation || "");
        setGrade(doc.grade || "");
        setSubject(doc.subject || "");
        setPlan({
          profile: doc.profile,
          inputs: {
            situation: doc.situation || "",
            grade: doc.grade || "",
            subject: doc.subject || "",
          },
          headline: body.headline || "",
          lead_with_strengths: body.lead_with_strengths || "",
          try_this_first: body.try_this_first || "",
          applied_strategies: body.applied_strategies || [],
          classroom_setup: body.classroom_setup || "",
          look_fors: body.look_fors || [],
          teacher_note: body.teacher_note || "",
          saved_plan_id: doc.id,
          created_at: doc.created_at,
        });
      } catch {
        /* swallow — failure leaves the empty generator visible */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, apiProfile, requestedPlanId]);

  // Phase 126.F — pull the caller's remaining free quota on mount so
  // the cap banner renders before they bother typing. Refreshes after
  // a successful generation so the post-generate state shows the new
  // "next available" countdown immediately.
  useEffect(() => {
    if (!user || !apiProfile) {
      setQuota(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase()}/classroom-support/${apiProfile}/quota`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setQuota(data);
      } catch {
        /* swallow — quota is non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, apiProfile, plan]);

  useDocumentMeta({
    title: meta
      ? `${meta.headline} — Atomic Edu`
      : "Classroom Support generator — Atomic Edu",
    description: meta?.tagline,
    type: "website",
  });

  const capped = !!(quota && !quota.exempt && quota.remaining === 0);

  const canSubmit = useMemo(
    () => situation.trim().length >= 3 && !busy && !capped,
    [situation, busy, capped],
  );

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#f9fafb] grid place-items-center text-[#1c2d3d]">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70">
            Not found
          </p>
          <h1 className="font-black text-3xl mt-3">Unknown profile</h1>
          <Link
            to="/classroom-support"
            className="text-sm font-bold text-[#1c2d3d] underline mt-4 inline-block"
            data-testid="generator-back-to-hub"
          >
            ← Back to the Hub
          </Link>
        </div>
      </div>
    );
  }

  const generate = async () => {
    if (!apiProfile || !canSubmit) return;
    setBusy(true);
    setPlan(null);
    try {
      const res = await fetch(`${apiBase()}/classroom-support/${apiProfile}/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ situation: situation.trim(), grade, subject }),
      });
      if (res.status === 401 || res.status === 403) {
        toast.error("Sign in to generate a plan (free).");
        return;
      }
      if (res.status === 429) {
        // Refresh quota so the banner shows next-available date.
        try {
          const quotaRes = await fetch(`${apiBase()}/classroom-support/${apiProfile}/quota`, {
            credentials: "include",
          });
          if (quotaRes.ok) setQuota(await quotaRes.json());
        } catch {
          /* non-blocking */
        }
        const body = await res.json().catch(() => null);
        const detail = body?.detail;
        toast.error(
          typeof detail === "object" && detail?.message
            ? detail.message
            : "You've used your free plans for this month.",
        );
        return;
      }
      if (res.status === 502) {
        toast.error("The generator hit a snag. Try again in a moment.");
        return;
      }
      if (!res.ok) throw new Error(`Generate failed (${res.status})`);
      const data = await res.json();
      setPlan(data);
      toast.success("Plan ready.");
    } catch (e) {
      toast.error(e?.message || "Generate failed.");
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = async (branded) => {
    if (!plan || !apiProfile) return;
    const setter = branded ? setExportingBranded : setExportingUnbranded;
    setter(true);
    try {
      const res = await fetch(`${apiBase()}/classroom-support/${apiProfile}/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, branded }),
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${apiProfile}-support-plan${branded ? "" : "-unbranded"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || "Export failed.");
    } finally {
      setter(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#f9fafb] text-[#1c2d3d]"
      data-testid={`classroom-support-generator-${profileKey}`}
    >
      <header className="border-b border-[#4a6984]/10 bg-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <Link
            to={PROFILE_TO_REF_PAGE[profileKey]}
            className="inline-flex items-center text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d]"
            data-testid="generator-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to the {PROFILE_REF_LABEL[profileKey]} guide
          </Link>
          <Link
            to="/classroom-support"
            className="text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d]"
          >
            Classroom Support Hub
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pt-12 pb-16">
        <p className="text-xs uppercase tracking-[0.28em] font-extrabold text-[#4a6984]/70 mb-3">
          Free generator · No student names · Saved to your library
        </p>
        <h1
          className="font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tight"
          data-testid="generator-title"
        >
          {meta.headline}
        </h1>
        <p className="mt-5 text-lg text-[#4a6984] leading-relaxed max-w-2xl">
          {meta.tagline}
        </p>

        {/* Auth wall */}
        {!authLoading && !user ? (
          <section
            className="mt-10 ae-card p-7 bg-white"
            data-testid="generator-auth-wall"
          >
            <div className="flex items-start gap-4">
              <span className="h-11 w-11 rounded-lg bg-[#96ded1]/20 grid place-items-center shrink-0">
                <LogIn className="h-5 w-5 text-[#1c2d3d]" />
              </span>
              <div className="flex-1">
                <h2 className="font-extrabold text-xl text-[#1c2d3d]">
                  Sign in to generate — it&apos;s free
                </h2>
                <p className="text-[#4a6984] mt-2 leading-relaxed">
                  The generator is free for any signed-in teacher. We
                  use the account so you can keep your generated plans
                  in your library and so we know who&apos;s using the Hub —
                  not for marketing.
                </p>
                <button
                  type="button"
                  onClick={signIn}
                  data-testid="generator-sign-in"
                  className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-[#1c2d3d] text-white text-sm font-extrabold hover:bg-[#0f1e2c] transition"
                >
                  <LogIn className="h-4 w-4" />
                  Sign in with Google
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {/* Phase 126.M — Exhausted-state card. Calm, teacher-to-colleague
            tone. Two buttons: Upgrade (links to /pricing) and Maybe later
            (dismisses the card). The card replaces the form when free
            users have used all 5 monthly plans. Paid + admin users never
            see it. Profile guides stay open regardless. */}
        {user && capped && !capDismissed ? (
          <section
            className="mt-10 ae-card p-7 sm:p-8 bg-white"
            data-testid="generator-cap-banner"
          >
            <h2
              className="font-extrabold text-2xl text-[#1c2d3d]"
              data-testid="generator-cap-headline"
            >
              You&apos;ve used your free plans for this month.
            </h2>
            <p className="text-[#4a6984] mt-3 leading-relaxed">
              They reset on the 1st. If you&apos;d like unlimited classroom
              support plans now — plus the full Atomic Edu toolkit — you
              can upgrade any time.
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Link
                to="/pricing"
                data-testid="generator-cap-upgrade"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1c2d3d] text-white text-sm font-extrabold hover:bg-[#0f1e2c] transition"
              >
                Upgrade →
              </Link>
              <button
                type="button"
                onClick={() => setCapDismissed(true)}
                data-testid="generator-cap-maybe-later"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#1c2d3d] text-sm font-bold border border-[#4a6984]/25 hover:bg-[#96ded1]/15 transition"
              >
                Maybe later
              </button>
            </div>
            <p className="text-sm text-[#4a6984]/80 mt-5 leading-relaxed">
              The profile guides stay open to you anytime, with no limit.{" "}
              <Link
                to="/classroom-support"
                className="underline decoration-[#96ded1] decoration-2 underline-offset-2 font-semibold text-[#1c2d3d] hover:text-[#0f1e2c]"
                data-testid="generator-cap-back-to-hub"
              >
                Back to the Hub →
              </Link>
            </p>
          </section>
        ) : null}

        {/* Form — hidden when cap banner is visible (spec: card replaces
            the generate action). After dismissal, the form returns with a
            calm indicator + disabled Generate button. */}
        {user && !(capped && !capDismissed) ? (
          <section className="mt-10" data-testid="generator-form">
            <div className="ae-card p-6 sm:p-8 bg-white">
              <label className="block text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-2">
                The situation in your words
              </label>
              <textarea
                value={situation}
                onChange={(e) => setSituation(e.target.value)}
                rows={5}
                placeholder={meta.placeholder}
                disabled={busy}
                data-testid="generator-situation"
                className="w-full px-4 py-3 rounded-lg border border-[#4a6984]/20 text-[#1c2d3d] resize-y focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
              />
              <p className="text-xs text-[#4a6984]/70 mt-2 italic" data-testid="generator-privacy-note">
                <strong className="text-[#1c2d3d] not-italic">No student names please.</strong>{" "}
                Describe the situation, not the child — the plan reads as
                &quot;the student&quot; throughout, and it goes into{" "}
                <span className="not-italic font-semibold text-[#1c2d3d]">your</span> library, not anyone else&apos;s.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-2">
                    Grade (optional)
                  </label>
                  <input
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    placeholder="e.g., 5"
                    disabled={busy}
                    data-testid="generator-grade"
                    className="w-full px-4 py-2.5 rounded-lg border border-[#4a6984]/20 text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-2">
                    Subject (optional)
                  </label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g., Language Arts"
                    disabled={busy}
                    data-testid="generator-subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-[#4a6984]/20 text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                {/* Phase 126.M — Calm remaining-count indicator for free
                    users only. No red, no warning icons. Paid users
                    (exempt=true) never see this. */}
                {quota && !quota.exempt ? (
                  <p
                    className="text-xs text-[#4a6984]/80"
                    data-testid="generator-quota-indicator"
                  >
                    {capped
                      ? `5 of 5 free plans used this month — resets on the 1st.`
                      : `${quota.remaining} of ${quota.limit} free plans left this month.`}
                  </p>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={generate}
                  disabled={!canSubmit}
                  data-testid="generator-generate"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#96ded1] hover:bg-[#7fd0c1] text-[#1c2d3d] font-extrabold text-sm disabled:opacity-50 transition"
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>Generate plan</>
                  )}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {/* Result */}
        {busy && !plan ? (
          <div
            data-testid="support-plan-generating"
            className="mt-6 flex items-center justify-center py-6"
          >
            <AtomLoader messages={GENERATOR_LOADER_MESSAGES} />
          </div>
        ) : null}
        {plan ? <PlanCard plan={plan} meta={meta} /> : null}
        {plan && plan.saved_plan_id ? (
          <section
            className="mt-5 ae-card p-4 sm:p-5 bg-[#96ded1]/10 border border-[#96ded1]/40 flex items-center gap-3"
            data-testid="generator-saved-banner"
          >
            <span className="h-9 w-9 rounded-lg bg-white grid place-items-center shrink-0">
              <Library className="h-5 w-5 text-[#1c2d3d]" />
            </span>
            <p className="text-sm text-[#1c2d3d] leading-snug">
              <strong>Saved to your library.</strong>{" "}
              <Link to="/dashboard?view=library" className="underline decoration-[#96ded1] decoration-2 underline-offset-2 hover:text-[#0f1e2c] font-semibold" data-testid="generator-saved-banner-link">
                Open library →
              </Link>
            </p>
          </section>
        ) : null}
        {plan ? (
          <ExportRow
            onBranded={() => exportPdf(true)}
            onUnbranded={() => exportPdf(false)}
            exportingBranded={exportingBranded}
            exportingUnbranded={exportingUnbranded}
          />
        ) : null}
        {plan && plan.saved_plan_id ? (
          <CompanionToolsRow
            planId={plan.saved_plan_id}
            variant="card"
            testidPrefix="generator-companion-tools"
          />
        ) : null}
      </main>
    </div>
  );
}

function PlanCard({ plan, meta }) {
  const Icon = meta.icon;
  return (
    <article
      className="mt-10 ae-card p-7 sm:p-10 bg-white"
      data-testid="generator-plan"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="h-10 w-10 rounded-lg bg-[#96ded1]/20 grid place-items-center">
          <Icon className="h-5 w-5 text-[#1c2d3d]" />
        </span>
        <h2 className="font-extrabold text-2xl text-[#1c2d3d] tracking-tight">
          {meta.label} plan
        </h2>
      </div>

      {plan.headline ? (
        <p className="text-lg text-[#1c2d3d] leading-relaxed font-semibold border-l-4 border-[#96ded1] pl-4 py-1">
          {plan.headline}
        </p>
      ) : null}

      <Block title="Lead with strengths" body={plan.lead_with_strengths} testid="plan-lead" />
      <Block title="Try this first" body={plan.try_this_first} testid="plan-try-first" />

      {plan.applied_strategies?.length ? (
        <section className="mt-7" data-testid="plan-strategies">
          <h3 className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-3">
            Applied strategies
          </h3>
          <ul className="space-y-4">
            {plan.applied_strategies.map((s, i) => (
              <li
                key={i}
                data-testid={`plan-strategy-${i}`}
                className="border-l-2 border-[#4a6984]/20 pl-4"
              >
                <p className="font-extrabold text-[#1c2d3d]">{s.title}</p>
                <p className="text-[#4a6984] mt-1 leading-relaxed">{s.in_practice}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Block title="Classroom setup" body={plan.classroom_setup} testid="plan-setup" />

      {plan.look_fors?.length ? (
        <section className="mt-7" data-testid="plan-look-fors">
          <h3 className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-3">
            Look-fors (observational only)
          </h3>
          <ul className="space-y-2">
            {plan.look_fors.map((lf, i) => (
              <li
                key={i}
                className="flex items-start text-[#4a6984] leading-relaxed"
              >
                <span className="ae-marker mt-2.5" />
                <span>{lf}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Block title="Teacher note" body={plan.teacher_note} testid="plan-teacher-note" />
    </article>
  );
}

function Block({ title, body, testid }) {
  if (!body) return null;
  return (
    <section className="mt-7" data-testid={testid}>
      <h3 className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-2">
        {title}
      </h3>
      <p className="text-[#4a6984] leading-relaxed">{body}</p>
    </section>
  );
}

function ExportRow({ onBranded, onUnbranded, exportingBranded, exportingUnbranded }) {
  return (
    <div
      className="mt-8 ae-card p-6 bg-white"
      data-testid="generator-export-row"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70">
            Download the plan
          </p>
          <p className="text-sm text-[#4a6984] mt-1.5 leading-relaxed max-w-md">
            Branded for your own files. Unbranded strips the Atomic Edu
            wordmark and footer so you can send it home to a parent or
            partner without it reading as a marketing artifact.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onBranded}
            disabled={exportingBranded || exportingUnbranded}
            data-testid="generator-export-branded"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#1c2d3d] text-white text-sm font-extrabold hover:bg-[#0f1e2c] disabled:opacity-50 transition"
          >
            {exportingBranded ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Branded PDF
          </button>
          <button
            type="button"
            onClick={onUnbranded}
            disabled={exportingBranded || exportingUnbranded}
            data-testid="generator-export-unbranded"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#1c2d3d] text-sm font-extrabold border border-[#4a6984]/30 hover:bg-[#96ded1]/10 disabled:opacity-50 transition"
          >
            {exportingUnbranded ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Unbranded (send-home)
          </button>
        </div>
      </div>
    </div>
  );
}
